import { LitElement, html } from 'lit';
import { cache } from 'lit/directives/cache';
import { unsafeHTML } from 'lit/directives/unsafe-html';

import '../fit-collapse/fit-collapse';
import '../fit-slider/fit-slider';
import '../fit-fav/fit-fav';
import '../fit-rating/fit-rating';

import { wordSlice, renderStars } from '../fit-style/fit-stars-utility';
import { BreakpointBehavior } from '../fit-style/fit-breakpoint-behavior';
import { FitStorageBehavior } from '../fit-storage/fit-storage-behavior';

import styles from './fit-search-result.css';

class FitSearchResult extends BreakpointBehavior(FitStorageBehavior(LitElement)) {
  static get is() { return 'fit-search-result'; }

  static get styles() { return styles; }

  render() {
    return html`
    <div class="essentials ${this.collapsed ? 'product-teaser--closed' : ''}">
      ${cache(!this.collapsed ? html`
        <a class="image" href="${this.appendParamsToUrl(this.hotel.Url, this.additionalParams)}" target="${this.isTargetBlank(this.hotel.NewWindow)}">
          <div class="image__ratio">
            <fit-slider one-item-per-frame="">
            ${this.hotel.Images.map((item) => html`
              <div class="fit-slider__slide">
                <figure class="product-teaser__image">
                  <img lazy-load-src="${item.NamedImageSizes.SearchResult}" alt="">
                  ${item.ImageOverlay ? html`
                    ${item.ImageOverlay.Html ? html`
                      <span class="product-teaser__overlay-text product-teaser__overlay-text-${item.ImageOverlay.HtmlPosition}">${unsafeHTML(item.ImageOverlay.Html)}</span>` : ''}
                    ${item.ImageOverlay.Image ? html`
                      <span class="product-teaser__overlay-image product-teaser__overlay-image-${item.ImageOverlay.ImagePosition}"><img src="${item.ImageOverlay.Image}" alt=""></span>` : ''}
                  ` : ''}
                </figure>
              </div>`)}
            </fit-slider>
            ${this.shortages.HotelTextTopLeft ? html`
            <span class="product-teaser__overlay-image product-teaser__overlay-image-top-right">${unsafeHTML(this.shortages.HotelTextTopLeft)}</span>` : ''}
          </div>
        </a>` : '')}
      <div class="description">
        <button @click="${this.toggle}" class="product-teaser__close product-teaser__close--search-result button button--link">×</button>
        <fit-fav class="product-teaser__fav fit-search-result" code="${this.hotel.Code}"></fit-fav>
        <h3 class="product-teaser__title">
          <span class="product-teaser__title-spacer"></span>
          <a href="${this.appendParamsToUrl(this.hotel.Url, this.additionalParams)}" target="${this.isTargetBlank(this.hotel.NewWindow)}">${wordSlice(this.hotel.Name, 0, -1)} <span class="product-teaser__title-unbreakable">${wordSlice(this.hotel.Name, -1)} <span class="product-teaser__subtitle--stars">${renderStars(this.hotel.Stars)}</span></span></a>
        </h3>
        <p class="product-teaser__location">
          <span class="product-teaser__country">${this.hotel.CountryName}</span>
          <span class="product-teaser__region">${this.hotel.RegionName}</span>
          <span class="product-teaser__city">${this.hotel.LocationName}</span>
          ${this.shortages.HotelTextTopRight && !this.collapsed ? html`<div class="product-teaser__text-top-right-shortages">${unsafeHTML(this.shortages.HotelTextTopRight)}</div>` : ''}
          </p>
          ${cache(!this.collapsed ? html`
            ${this.hotel.RatingValue > 0 ? html`
              <fit-rating class="product-teaser__rating" text="${this.hotel.RatingText}" value="${this.hotel.RatingValue}" count="${this.hotel.RatingCount}" decimal-separator-label="${this.labels.DecimalSeparatorLabel}" count-label="${this.labels.RatingCount}"></fit-rating>
            ` : ''}
            <p class="product-teaser__description">${this.hotel.Subtitle}</p>
            ${this.shortages.HotelImageBottomLeft ? html`<p class="product-teaser__image-bottom-left-shortages">${unsafeHTML(this.shortages.HotelImageBottomLeft)}</p>` : ''}
          ` : '')}
        </div>
      </div>
      ${cache(!this.collapsed ? html`
        <div class="programs">
        ${this.programs.map((item) => html`
          <fit-collapse group-identifier="fit-search-result|${this.hotel.Code}">
            <div class="product-teaser__bottom" no-description="${!item.Description}" slot="trigger-outside">
              <div class="product-teaser__program-info">
                <h4 class="product-teaser__program">${item.Name}</h4>
                <p class="product-teaser__info">${item.Offer.PriceInformation}</p>
              </div>
              <p class="product-teaser__price" ?hidden="${this.showPriceButton}">${unsafeHTML(item.Offer.PriceHtml)}</p>
              <!-- smaller screens -->
              <div ?hidden="${!this.showPriceButton}" class="product-teaser__button-container">
                <div class="product-teaser__toggle-buttons" ?hidden="${!item.Description}">
                  <button class="button button--inverted product-teaser__toggle-button product-teaser__toggle-button--open" ?small-button="${!this.containsOriginalPrice(item.Offer.PriceHtml)}"><i class="fa fa-chevron-down"></i></button>
                  <button class="button button--inverted product-teaser__toggle-button product-teaser__toggle-button--close" ?small-button="${!this.containsOriginalPrice(item.Offer.PriceHtml)}"><i class="fa fa-chevron-up"></i></button>
                </div>
                <a href="${this.appendParamsToUrl(item.Url, this.additionalParams)}" target="${this.isTargetBlank(item.NewWindow)}" class="button product-teaser__price-button" ?hidden="${!this.containsOriginalPrice(item.Offer.PriceHtml)}">${unsafeHTML(item.Offer.PriceHtml)}</a>
                <div class="product-teaser__price-group" ?hidden="${this.containsOriginalPrice(item.Offer.PriceHtml)}">
                  <del class="product-teaser__striked-price">${this.originalPrice(item.Offer.PriceHtml)}</del>
                  <a href="${this.appendParamsToUrl(item.Url, this.additionalParams)}" class="button button--small product-teaser__price-button" ?hidden="${!this.showPriceButton}">${unsafeHTML(this.offerPrice(item.Offer.PriceHtml))}</a>
                </div>
              </div>
              <!-- bigger screens -->
              <div class="product-teaser__buttons" ?hidden="${this.showPriceButton}">
                <div class="product-teaser__toggle-buttons" ?hidden="${!item.Description}">
                  <button class="button button--inverted product-teaser__toggle-button product-teaser__toggle-button--open"><span>${this.labels.OpenProgram}</span><i class="fa fa-chevron-down"></i></button>
                  <button class="button button--inverted product-teaser__toggle-button product-teaser__toggle-button--close"><span>${this.labels.CloseProgram}</span><i class="fa fa-chevron-up"></i></button>
                </div>
                <a href="${this.appendParamsToUrl(item.Url, this.additionalParams)}" target="${this.isTargetBlank(item.NewWindow)}" class="button button--strong" @click="${(e) => e.stopPropagation()}">${this.labels.CtaProgram}</a>
              </div>
            </div>
            ${item.Description ? html`
              <div class="product-teaser__program-description">
                ${unsafeHTML(item.Description)}
              </div>
            ` : ''}
            <div ?hidden="${!item.IsLongDescripton}" class="product-teaser__program-description-button"><a href="${this.appendParamsToUrl(item.Url, this.additionalParams)}" target="${this.isTargetBlank(item.NewWindow)}" class="button button--strong">${this.labels.CtaProgram}</a></div>
          </fit-collapse>
        `)}
        </div>
      ` : '')}
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" style="visibility: hidden; position: absolute; z-index: -1"> <defs> <linearGradient id="corner-ribbon-gradient" x1="0%" y1="100%" x2="70%" y2="0%" x3="0%" y3="100%"> <stop offset="0%" style="stop-color:rgb(236,88,64);stop-opacity:1"></stop> <stop offset="50%" style="stop-color:rgb(255,144,125);stop-opacity:1"></stop> <stop offset="100%" style="stop-color:rgb(236,88,64);stop-opacity:1"></stop> </linearGradient> </defs> </svg>
    `;
  }

  static get properties() {
    return {
      hotel: {
        type: Object,
      },

      collapsed: {
        type: Boolean,
      },

      baseUrl: {
        type: String,
      },

      additionalParams: {
        type: String,
      },

      showPriceButton: {
        type: Boolean,
      },

      labels: {
        type: Object,
      },
    };
  }

  get storageName() {
    return `search-result-collapse|${this.hotel.Code}`;
  }

  set storageValue(collapsed) {
    this.collapsed = Boolean(collapsed);
  }

  constructor(props) {
    super(props);
    this.type = 'Hotel';
    this.collapsed = false;
    this.showPriceButton = true;
    this.labels = {};
    this.fireInterestEvent = this.fireInterestEvent.bind(this);
    this.updateShowPriceButton = this.updateShowPriceButton.bind(this);

    this.mql = window.matchMedia('(max-width: 45rem)');
    this.mql.onchange = this.updateShowPriceButton;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.fireInterestEvent);
    this.addEventListener('mouseleave', this.fireInterestEvent);

    this.showPriceButton = this.mql.matches;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.fireInterestEvent);
    this.removeEventListener('mouseleave', this.fireInterestEvent);
  }

  toggle(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }
    this.collapsed = !this.collapsed;
    this.saveStorageValue(this.collapsed);
  }

  get programs() {
    const programs = [];
    if (this.hotel === undefined || this.type === undefined) {
      return programs;
    }
    this.hotel.Programs.forEach((program) => {
      if (program.OfferHotel !== undefined) {
        const newProgram = { ...program };
        const offer = program.OfferHotel;
        for (const key in newProgram) {
          if (key.substr(0, 5) === 'Offer') {
            delete newProgram[key];
          }
        }
        newProgram.Offer = offer;
        const linesInDescription = newProgram.Description.match(/<li>/g);
        newProgram.IsLongDescripton = linesInDescription ? linesInDescription.length >= 12 : false;
        programs.push(newProgram);
      }
    });
    return programs;
  }

  get shortages() {
    const shortages = {};
    if (this.hotel.Info !== undefined) {
      this.hotel.Info.forEach((info) => {
        if (info.Position === undefined || info.Shortages === undefined) {
          return;
        }

        if (shortages[info.Position] === undefined) {
          shortages[info.Position] = '';
        }
        shortages[info.Position] = info.Shortages.reduce((acc, shortage) => `${acc} ${shortage.Message}`,
          '');
      });
    }
    return shortages;
  }

  updateShowPriceButton(event) {
    this.showPriceButton = event.matches;
  }

  appendParamsToUrl(url, additionalParams) {
    if (additionalParams) {
      url = url + (!url.includes('?') ? '?' : '&') + additionalParams;
    }
    return url;
  }

  isTargetBlank(openInNewWindow) {
    return openInNewWindow ? '_blank' : '';
  }

  containsOriginalPrice(priceHtml) {
    return priceHtml.indexOf('<del>');
  }

  originalPrice(priceHtml) {
    const price = priceHtml.split(/<\/?del>/);
    return price.length === 3 ? price[1] : '';
  }

  offerPrice(priceHtml) {
    const price = priceHtml.split(/<\/?del>/);
    return price.length === 3 ? price[2] : price[0];
  }

  fireInterestEvent(event) {
    if (event.type === 'mouseenter') {
      this.dispatchEvent(new CustomEvent('fit-search-result-interest-begin', { detail: this.hotel, bubbles: true, composed: true }));
    } else {
      this.dispatchEvent(new CustomEvent('fit-search-result-interest-end', { detail: this.hotel, bubbles: true, composed: true }));
    }
  }
}

customElements.define(FitSearchResult.is, FitSearchResult);
